import React, { useEffect, useState } from 'react';
import {
    incompatibilityDetails,
    suggestVerification,
    toUserErrorMessage,
} from '../../../../services/BookkeepingTemplateService';
import { journalEntriesFromProposal, proposalFromTemplateResponse } from '../../../../services/BookingProposalService';
import useBookingProposalReview from '../../../../hooks/useBookingProposalReview';
import PreviewStep from './PreviewStep';

/**
 * Step 4 (PREVIEW) wrapper for new-shape templates.
 *
 * This is INTENTIONALLY thin — its only job is to call the backend
 * /suggest endpoint with the user's filled-in inputs and pre-populate
 * `entry.customJournalEntries` from the result. The full PreviewStep
 * underneath then renders the entries with all its existing UX:
 * editable journal-entry modal, balance display, account-name
 * resolution, attachments preview, etc.
 *
 * Why this wrapper exists rather than baking /suggest into PreviewStep
 * itself: PreviewStep is shape-agnostic by design (it consumes
 * customJournalEntries) and we want to keep it that way. The
 * shape-aware logic — "call /suggest because the body's lines need
 * server-side evaluation" — lives here at the wizard layer.
 */
function NewTemplatePreviewStep({
    entry,
    onChange,
    companyId,
    templates,
    accounts,
    allAccounts = null,
    accountScope = null,
    accountsLoading,
    accountsError,
    getAccountByNumber,
    getAccountsByType,
    searchAccounts,
}) {
    const template = entry?.templateData;
    const [suggestError, setSuggestError] = useState(null);
    const { selectProposal, reset: resetProposal } = useBookingProposalReview({
        transaction: { amount: 0 },
        source: 'manual',
    });

    useEffect(() => {
        if (!template || !template.body || !companyId) return undefined;
        // Wait until every required input has a value — calling
        // /suggest without them returns a 400 we don't want to surface.
        const inputs = template.body.inputs || [];
        const missingRequired = inputs.some(
            (i) => i.required && (
                entry.parameters?.[i.name] === undefined
                || entry.parameters?.[i.name] === ''
                || entry.parameters?.[i.name] === null
            ),
        );
        if (missingRequired) return undefined;

        let cancelled = false;
        setSuggestError(null);
        suggestVerification(companyId, template.template_id, entry.parameters || {})
            .then((r) => {
                if (cancelled) return;
                // The manual wizard consumes exactly the same canonical
                // proposal contract as bank and Skatteverket entry points.
                // PreviewStep still owns editing/rendering of journal rows.
                const candidate = proposalFromTemplateResponse(r, template, {
                    transaction: { amount: 0 },
                    source: 'manual',
                });
                if (r && r.ok && candidate) {
                    const proposal = selectProposal(candidate);
                    const journalEntries = journalEntriesFromProposal(proposal, entry.description || '');
                    onChange({
                        ...entry,
                        bookingProposal: proposal,
                        customJournalEntries: journalEntries,
                        templateIncompatibility: null,
                    });
                } else if (entry.customJournalEntries) {
                    resetProposal();
                    onChange({
                        ...entry,
                        bookingProposal: null,
                        customJournalEntries: null,
                        templateIncompatibility: null,
                    });
                }
            })
            .catch((err) => {
                if (cancelled) return;
                setSuggestError(err);
                resetProposal();
                onChange({
                    ...entry,
                    bookingProposal: null,
                    customJournalEntries: null,
                    templateIncompatibility: incompatibilityDetails(err),
                });
            });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId, template?.template_id, JSON.stringify(entry.parameters)]);

    return (
        <>
            {suggestError ? (
                <div className="alert alert-danger" role="alert">
                    {toUserErrorMessage(suggestError)}
                    {incompatibilityDetails(suggestError).map((detail, index) => (
                        <div key={`${detail.path}-${index}`} className="small mt-1">
                            {[detail.path, detail.reference, detail.chartVersion, detail.reasonText]
                                .filter(Boolean).join(' — ')}
                        </div>
                    ))}
                </div>
            ) : null}
            <PreviewStep
                entry={entry}
                onChange={onChange}
                templates={templates}
                accounts={accounts}
                allAccounts={allAccounts}
                accountScope={accountScope}
                accountsLoading={accountsLoading}
                accountsError={accountsError}
                getAccountByNumber={getAccountByNumber}
                getAccountsByType={getAccountsByType}
                searchAccounts={searchAccounts}
            />
        </>
    );
}

export default NewTemplatePreviewStep;
